import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertTriangle, X, Bell, MessageCircle, Send, ChevronLeft, User, Check, CheckCheck, XCircle, RotateCcw, Trash2 } from 'lucide-react';
import FullscreenButton from '@/components/FullscreenButton';
import { formatUKTime } from '@/utils/ukTime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ControlDeskScreen() {
  const [pinEntered, setPinEntered] = useState(false);
  const [pin, setPin] = useState('');
  const [competitions, setCompetitions] = useState([]);
  const [competitionId, setCompetitionId] = useState('');
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [isFlashing, setIsFlashing] = useState(false);
  const intervalRef = useRef(null);
  const audioRef = useRef(null);
  const prevAlertsCountRef = useRef(0);

  // Spectator support state
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newReply, setNewReply] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [spectatorTyping, setSpectatorTyping] = useState(false);
  const [spectatorReadAt, setSpectatorReadAt] = useState(null);
  const messagesEndRef = useRef(null);
  const prevConvCountRef = useRef(0);
  const typingTimeoutRef = useRef(null);
  const lastTypingSentRef = useRef(0);

  // Operator selection dialog
  const [showOperatorDialog, setShowOperatorDialog] = useState(false);
  const [pendingConversation, setPendingConversation] = useState(null);
  
  // Toggle for showing closed conversations
  const [showClosed, setShowClosed] = useState(false);

  const apparatusNames = {
    vault: 'VAULT',
    bars: 'UNEVEN BARS',
    beam: 'BALANCE BEAM',
    floor: 'FLOOR'
  };

  useEffect(() => {
    const fetchCompetitions = async () => {
      try {
        const response = await axios.get(`${API}/public/competitions`);
        setCompetitions(response.data.competitions || []);
      } catch (error) {
        console.error('Error fetching competitions:', error);
      }
    };
    fetchCompetitions();
  }, []);

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    
    if (pin !== '2507') {
      toast.error('Invalid PIN');
      setPin('');
      return;
    }

    if (!competitionId) {
      toast.error('Please select a competition');
      return;
    }

    const comp = competitions.find(c => c.id === competitionId);
    setSelectedCompetition(comp);
    setPinEntered(true);
    toast.success('Control Desk Screen Active');
  };

  // Poll for alerts
  useEffect(() => {
    if (!pinEntered || !competitionId) return;

    const fetchAlerts = async () => {
      try {
        const response = await axios.get(`${API}/help-alerts/${competitionId}`);
        const newAlerts = response.data.alerts || [];
        
        if (newAlerts.length > prevAlertsCountRef.current) {
          setIsFlashing(true);
          if (audioRef.current) {
            audioRef.current.play().catch(e => console.log('Audio play failed:', e));
          }
        }
        
        prevAlertsCountRef.current = newAlerts.length;
        setAlerts(newAlerts);
        
        if (newAlerts.length === 0) {
          setIsFlashing(false);
        }
      } catch (error) {
        console.error('Error fetching alerts:', error);
      }
    };

    fetchAlerts();
    intervalRef.current = setInterval(fetchAlerts, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [pinEntered, competitionId]);

  // Poll for spectator conversations
  useEffect(() => {
    if (!pinEntered) return;

    const fetchConversations = async () => {
      try {
        const response = await axios.get(`${API}/spectator-support/conversations`, {
          params: { include_closed: showClosed }
        });
        const convs = response.data.conversations || [];
        
        // Check for new unread messages
        const hasUnread = convs.some(c => c.unreadBySupport > 0);
        if (hasUnread && prevConvCountRef.current > 0) {
          toast.info('New spectator message!');
        }
        prevConvCountRef.current = convs.length;
        setConversations(convs);
      } catch (error) {
        console.error('Error fetching conversations:', error);
      }
    };

    fetchConversations();
    const interval = setInterval(fetchConversations, 3000);
    return () => clearInterval(interval);
  }, [pinEntered, showClosed]);

  // Fetch messages and status for selected conversation
  useEffect(() => {
    if (!selectedConversation) return;

    const fetchData = async () => {
      try {
        const msgResponse = await axios.get(`${API}/spectator-support/messages/${selectedConversation.id}`);
        setMessages(msgResponse.data.messages || []);
        
        await axios.put(`${API}/spectator-support/conversation/${selectedConversation.id}/read`, {
          reader: 'support'
        });
        
        const statusResponse = await axios.get(`${API}/spectator-support/conversation/${selectedConversation.id}/status`);
        setSpectatorTyping(statusResponse.data.spectatorTyping);
        setSpectatorReadAt(statusResponse.data.spectatorReadAt);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 2000);
    return () => clearInterval(interval);
  }, [selectedConversation]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, spectatorTyping]);

  // Handle clicking on a conversation
  const handleSelectConversation = (conv) => {
    if (!conv.operatorName) {
      // First time opening - show operator selection dialog
      setPendingConversation(conv);
      setShowOperatorDialog(true);
    } else {
      // Already has operator assigned
      setSelectedConversation(conv);
    }
  };

  // Handle operator selection
  const handleOperatorSelect = async (operatorName) => {
    if (!pendingConversation) return;
    
    try {
      // Set operator
      await axios.put(`${API}/spectator-support/conversation/${pendingConversation.id}/operator`, {
        operatorName
      });
      
      // Send welcome message
      const compName = selectedCompetition?.title || 'the competition';
      const welcomeMessage = `Hi! Welcome to ${compName}. You're through to ${operatorName} at Scorz Support. If you have any problems or questions, please feel free to let me know here!`;
      
      await axios.post(`${API}/spectator-support/message`, {
        conversationId: pendingConversation.id,
        sender: 'support',
        message: welcomeMessage
      });
      
      // Update local state and open conversation
      const updatedConv = { ...pendingConversation, operatorName };
      setSelectedConversation(updatedConv);
      setShowOperatorDialog(false);
      setPendingConversation(null);
      
      toast.success(`Welcome message sent as ${operatorName}`);
    } catch (error) {
      console.error('Error setting operator:', error);
      toast.error('Failed to set operator');
    }
  };

  // Close conversation
  const handleCloseConversation = async () => {
    if (!selectedConversation) return;
    
    try {
      await axios.put(`${API}/spectator-support/conversation/${selectedConversation.id}/close`);
      setSelectedConversation(null);
      toast.success('Conversation closed');
      
      // Refresh conversations list
      const response = await axios.get(`${API}/spectator-support/conversations`, {
        params: { include_closed: showClosed }
      });
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error closing conversation:', error);
      toast.error('Failed to close conversation');
    }
  };

  // Reopen conversation
  const handleReopenConversation = async () => {
    if (!selectedConversation) return;
    
    try {
      await axios.put(`${API}/spectator-support/conversation/${selectedConversation.id}/reopen`);
      
      // Update local state
      setSelectedConversation({ ...selectedConversation, closed: false });
      toast.success('Conversation reopened');
      
      // Refresh conversations list
      const response = await axios.get(`${API}/spectator-support/conversations`, {
        params: { include_closed: showClosed }
      });
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error reopening conversation:', error);
      toast.error('Failed to reopen conversation');
    }
  };

  // Permanently delete conversation
  const handleDeleteConversation = async () => {
    if (!selectedConversation) return;
    
    // Confirm deletion
    if (!window.confirm('Are you sure you want to permanently delete this conversation? This cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/spectator-support/conversation/${selectedConversation.id}`);
      setSelectedConversation(null);
      setMessages([]);
      toast.success('Conversation permanently deleted');
      
      // Refresh conversations list
      const response = await axios.get(`${API}/spectator-support/conversations`, {
        params: { include_closed: showClosed }
      });
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error deleting conversation:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to delete conversation';
      toast.error(errorMsg);
    }
  };

  // Send typing indicator
  const sendTypingIndicator = useCallback(async (isTyping) => {
    if (!selectedConversation) return;
    
    const now = Date.now();
    if (isTyping && now - lastTypingSentRef.current < 2000) return;
    lastTypingSentRef.current = now;
    
    try {
      await axios.put(`${API}/spectator-support/conversation/${selectedConversation.id}/typing`, {
        typer: 'support',
        isTyping
      });
    } catch (error) {
      console.error('Failed to send typing indicator:', error);
    }
  }, [selectedConversation]);

  const handleReplyChange = (e) => {
    setNewReply(e.target.value);
    
    if (e.target.value.trim()) {
      sendTypingIndicator(true);
      
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        sendTypingIndicator(false);
      }, 3000);
    } else {
      sendTypingIndicator(false);
    }
  };

  const handleDismiss = async (alertId) => {
    try {
      await axios.delete(`${API}/help-alerts/${alertId}`);
      const remaining = alerts.filter(a => a.id !== alertId);
      setAlerts(remaining);
      prevAlertsCountRef.current = remaining.length;
      toast.success('Alert dismissed');
      
      if (remaining.length === 0) {
        setIsFlashing(false);
      }
    } catch (error) {
      console.error('Error dismissing alert:', error);
      toast.error('Failed to dismiss alert');
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!newReply.trim() || !selectedConversation || sendingReply) return;

    sendTypingIndicator(false);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

    setSendingReply(true);
    try {
      await axios.post(`${API}/spectator-support/message`, {
        conversationId: selectedConversation.id,
        sender: 'support',
        message: newReply.trim()
      });
      
      setNewReply('');
      setSpectatorReadAt(null);
      
      const response = await axios.get(`${API}/spectator-support/messages/${selectedConversation.id}`);
      setMessages(response.data.messages || []);
      toast.success('Reply sent');
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '';
    return formatUKTime(isoString, true);
  };

  const formatTimeShort = (isoString) => {
    if (!isoString) return '';
    return formatUKTime(isoString, false);
  };

  if (!pinEntered) {
    return (
      <div className="h-screen bg-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl border-gray-200">
          <CardHeader className="text-center pb-4">
            <img src="/scorz-logo.png" alt="Scorz Logo" className="h-14 mx-auto mb-3" />
            <CardTitle className="text-xl font-bold text-black">Control Desk</CardTitle>
            <p className="text-gray-500 text-sm">Monitor help alerts from judges</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePinSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Competition</label>
                <Select value={competitionId} onValueChange={setCompetitionId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a competition..." />
                  </SelectTrigger>
                  <SelectContent>
                    {competitions.map((comp) => (
                      <SelectItem key={comp.id} value={comp.id}>{comp.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PIN</label>
                <Input
                  type="password"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  placeholder="Enter PIN"
                  className="text-center text-xl tracking-widest"
                  maxLength={4}
                />
              </div>
              <Button type="submit" className="w-full bg-black hover:bg-gray-800">
                Access Control Desk
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalUnread = conversations.reduce((sum, c) => sum + (c.unreadBySupport || 0), 0);
  
  const lastSupportMessage = selectedConversation 
    ? [...messages].reverse().find(m => m.sender === 'support')
    : null;

  return (
    <div className={`h-screen flex transition-colors duration-300 ${
      isFlashing && alerts.length > 0 ? 'bg-red-600 animate-pulse' : 'bg-gray-100'
    }`}>
      <audio ref={audioRef} src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBjiP1O/" />
      
      {/* Main Content - Judge Alerts */}
      <div className="flex-1 flex flex-col p-4 min-w-0">
        <div className="flex items-center justify-between mb-4 shrink-0">
          <div className="flex items-center gap-3">
            <img src="/scorz-logo.png" alt="Scorz" className="h-10" />
            <div>
              <h1 className={`text-2xl font-bold ${isFlashing && alerts.length > 0 ? 'text-white' : 'text-black'}`}>
                Control Desk
              </h1>
              <p className={`text-xs ${isFlashing && alerts.length > 0 ? 'text-red-100' : 'text-gray-600'}`}>
                {selectedCompetition?.title}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {alerts.length > 0 && (
              <div className={`px-4 py-2 rounded-lg text-lg font-bold ${
                isFlashing ? 'bg-white text-red-600' : 'bg-black text-white'
              }`}>
                {alerts.length} Alert{alerts.length !== 1 ? 's' : ''}
              </div>
            )}
            <FullscreenButton />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          {alerts.length === 0 ? (
            <Card className="bg-white border-gray-200 shadow-lg h-full flex items-center justify-center">
              <CardContent className="text-center">
                <Bell className="w-16 h-16 mx-auto mb-3 text-gray-300" />
                <p className="text-xl text-gray-600 font-medium">No active help alerts</p>
                <p className="text-gray-400 text-sm">Monitoring for help requests...</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {alerts.map((alert) => (
                <Card 
                  key={alert.id} 
                  className={`shadow-lg ${isFlashing ? 'bg-white border-white' : 'bg-red-600 border-red-500'}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className={`w-10 h-10 ${isFlashing ? 'text-red-600' : 'text-white'}`} />
                        <div>
                          <h3 className={`text-2xl font-bold ${isFlashing ? 'text-black' : 'text-white'}`}>
                            {apparatusNames[alert.apparatus]}
                            {alert.panel !== 'single' && (
                              <span className={`ml-2 text-lg ${isFlashing ? 'text-gray-700' : 'text-yellow-300'}`}>
                                Panel {alert.panel}
                              </span>
                            )}
                          </h3>
                          <p className={`text-sm ${isFlashing ? 'text-gray-600' : 'text-red-100'}`}>
                            {formatTime(alert.createdAt)}
                          </p>
                        </div>
                      </div>
                      <Button
                        onClick={() => handleDismiss(alert.id)}
                        size="lg"
                        className={`font-semibold ${
                          isFlashing ? 'bg-black text-white hover:bg-gray-800' : 'bg-white text-black hover:bg-gray-100'
                        }`}
                      >
                        <X className="w-5 h-5 mr-1" /> Dismiss
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Spectator Support Panel */}
      <div className="w-80 bg-white border-l border-gray-200 flex flex-col shrink-0">
        <div className="p-3 border-b bg-gray-50 shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-gray-600" />
              <h2 className="font-bold text-sm text-gray-800">Spectator Support</h2>
            </div>
            {totalUnread > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                {totalUnread}
              </span>
            )}
          </div>
          {/* Toggle for closed conversations */}
          <div className="flex items-center gap-2 mt-2">
            <button
              data-testid="toggle-closed-conversations"
              onClick={() => setShowClosed(!showClosed)}
              className={`text-xs px-2 py-1 rounded transition-colors ${
                showClosed 
                  ? 'bg-gray-800 text-white' 
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              {showClosed ? 'Viewing Closed' : 'Show Closed'}
            </button>
          </div>
        </div>

        {!selectedConversation ? (
          <div className="flex-1 overflow-y-auto min-h-0">
            {conversations.length === 0 ? (
              <div className="p-4 text-center text-gray-400">
                <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-xs">No messages yet</p>
              </div>
            ) : (
              conversations.map((conv) => (
                <div
                  key={conv.id}
                  data-testid={`conversation-item-${conv.id}`}
                  onClick={() => handleSelectConversation(conv)}
                  className={`p-3 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                    conv.unreadBySupport > 0 ? 'bg-blue-50' : ''
                  } ${conv.closed ? 'opacity-60' : ''}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                      <User className="w-3 h-3 text-gray-400" />
                      <span className="font-semibold text-xs">{conv.spectatorName}</span>
                      {conv.closed && (
                        <span data-testid="conversation-closed-badge" className="text-xs text-gray-400 bg-gray-200 px-1 rounded">Closed</span>
                      )}
                      {conv.spectatorTyping && !conv.closed && (
                        <span className="text-xs text-green-600 italic">typing...</span>
                      )}
                    </div>
                    <span className="text-xs text-gray-400">{formatTimeShort(conv.lastMessageAt)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-600 truncate flex-1 pr-2">
                      {conv.lastSender === 'support' ? 'You: ' : ''}{conv.lastMessage || 'No messages'}
                    </p>
                    {conv.unreadBySupport > 0 && (
                      <span className="bg-blue-500 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center">
                        {conv.unreadBySupport}
                      </span>
                    )}
                  </div>
                  {conv.operatorName && (
                    <p className="text-xs text-gray-400 mt-1">Handled by {conv.operatorName}</p>
                  )}
                </div>
              ))
            )}
          </div>
        ) : (
          <>
            <div className="p-2 border-b bg-gray-50 flex items-center gap-2 shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedConversation(null)}
                className="p-1 h-auto"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <p className="font-semibold text-xs truncate">{selectedConversation.spectatorName}</p>
                  {selectedConversation.closed && (
                    <span className="text-xs text-gray-400 bg-gray-200 px-1 rounded">Closed</span>
                  )}
                </div>
                <p className="text-xs text-gray-400">
                  {spectatorTyping ? (
                    <span className="text-green-600">typing...</span>
                  ) : selectedConversation.operatorName ? (
                    `Handled by ${selectedConversation.operatorName}`
                  ) : (
                    'Spectator'
                  )}
                </p>
              </div>
              {selectedConversation.closed ? (
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleReopenConversation}
                    className="p-1 h-auto text-green-600 hover:text-green-700 hover:bg-green-50"
                    title="Reopen conversation"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDeleteConversation}
                    className="p-1 h-auto text-red-500 hover:text-red-700 hover:bg-red-50"
                    title="Permanently delete conversation"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCloseConversation}
                  className="p-1 h-auto text-red-500 hover:text-red-700 hover:bg-red-50"
                  title="Close conversation"
                >
                  <XCircle className="w-4 h-4" />
                </Button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-2 space-y-2 bg-gray-50 min-h-0">
              {messages.length === 0 ? (
                <p className="text-center text-gray-400 text-xs py-4">No messages</p>
              ) : (
                messages.map((msg) => {
                  const isLastSupportMsg = lastSupportMessage?.id === msg.id;
                  
                  return (
                    <div key={msg.id}>
                      <div className={`flex ${msg.sender === 'support' ? 'justify-end' : 'justify-start'}`}>
                        <div
                          className={`max-w-[85%] rounded-xl px-3 py-1.5 ${
                            msg.sender === 'support'
                              ? 'bg-black text-white rounded-br-sm'
                              : 'bg-white text-gray-800 rounded-bl-sm shadow-sm'
                          }`}
                        >
                          <p className="text-xs whitespace-pre-wrap">{msg.message}</p>
                          <p className="text-xs mt-0.5 opacity-60">{formatTimeShort(msg.createdAt)}</p>
                        </div>
                      </div>
                      
                      {msg.sender === 'support' && isLastSupportMsg && (
                        <div className="flex items-center justify-end gap-1 mt-0.5 pr-1">
                          {spectatorReadAt ? (
                            <>
                              <CheckCheck className="w-3 h-3 text-blue-500" />
                              <span className="text-xs text-blue-500">Read {formatTimeShort(spectatorReadAt)}</span>
                            </>
                          ) : (
                            <>
                              <Check className="w-3 h-3 text-gray-400" />
                              <span className="text-xs text-gray-400">Sent</span>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
              
              {spectatorTyping && (
                <div className="flex justify-start">
                  <div className="bg-white text-gray-600 rounded-xl rounded-bl-sm shadow-sm px-3 py-1.5">
                    <div className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            <form onSubmit={handleSendReply} className="p-2 border-t bg-white shrink-0">
              <div className="flex gap-2">
                <Input
                  value={newReply}
                  onChange={handleReplyChange}
                  placeholder="Reply..."
                  className="flex-1 text-xs h-8"
                  disabled={sendingReply}
                />
                <Button 
                  type="submit" 
                  size="sm"
                  className="bg-black hover:bg-gray-800 h-8 px-3"
                  disabled={!newReply.trim() || sendingReply}
                >
                  <Send className="w-3 h-3" />
                </Button>
              </div>
            </form>
          </>
        )}
      </div>

      {/* Operator Selection Dialog */}
      <Dialog open={showOperatorDialog} onOpenChange={setShowOperatorDialog}>
        <DialogContent className="sm:max-w-md" data-testid="operator-selection-dialog">
          <DialogHeader>
            <DialogTitle>Who&apos;s responding?</DialogTitle>
            <DialogDescription>
              Select your name to send a welcome message to {pendingConversation?.spectatorName}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 mt-4">
            <Button
              data-testid="select-operator-freddie"
              onClick={() => handleOperatorSelect('Freddie')}
              className="h-14 text-lg bg-black hover:bg-gray-800"
            >
              Freddie
            </Button>
            <Button
              data-testid="select-operator-ewan"
              onClick={() => handleOperatorSelect('Ewan')}
              className="h-14 text-lg bg-black hover:bg-gray-800"
            >
              Ewan
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
